'use client'

import { useRef, useEffect, useState, useMemo } from 'react'
import { keyBy } from 'lodash'
import { Chip, Text, Divider, Icon } from '@vinted/web-ui'
import { Filters16, X24 } from '@vinted/monochrome-icons'

import { useTrackAbTestCallback } from '@marketplace-web/feature-flags/ab-tests-data'
import { reloadPage } from '@marketplace-web/browser/browser-navigation-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { useLatestCallback } from '@marketplace-web/shared/ui-state-util'
import {
  addSelectedFilter,
  removeSelectedFilter,
  removeSelectedFilters,
  DynamicFilterModel,
  ExperimentModel,
  SelectedDynamicFilterModel,
  PriceRange,
  DisplayType,
  DynamicExposeType,
  FilterModalNames,
  OptionType,
  SelectionType,
  SortByOption,
  Filter,
  trackExperimentsByType,
} from '@marketplace-web/catalog/catalog-data'
import {
  useCatalogContext,
  DynamicFilterReducerAction,
  FilterReducerAction,
  useCatalogConfigurationContext,
  clickEvent,
  clickFilterEvent,
} from '@marketplace-web/catalog/catalog-page-data'
import { viewFilterEvent } from '@marketplace-web/catalog/catalog-filters-data'

import useFacetChangeHandler from '../../../hooks/useFacetChangeHandler'
import useFetchDynamicFiltersFacets from '../../../hooks/useFetchDynamicFiltersFacets'
import useFetchCatalogBrand from '../../../hooks/useFetchCatalogBrand'
import useFetchDynamicFilters from '../../../hooks/useFetchDynamicFilters'
import SortFilter from './SortFilter'
import CatalogFilter from './CatalogFilter'
import PriceFilter from './PriceFilter'
import ListFilterModal from '../common/DynamicFilters/ListFilter/ListFilterModal'
import SearchFilterModal from '../common/DynamicFilters/SearchFilter/SearchFilterModal'
import GridFilterModal from '../common/DynamicFilters/GridFilter/GridFilterModal'
import FilterModal from '../common/FilterModal'
import FilterModalNavigation from '../common/FilterModalNavigation'

type Props = {
  learnMoreUrl: string | null
}

type SharedFilterProps = {
  isFilterCellVisible: boolean
  onModalOpen: () => void
  onModalClose: () => void
}

const MobileFilters = ({ learnMoreUrl }: Props) => {
  const translate = useTranslate('catalog.filters')
  const { track } = useTracking()
  const fetchDynamicFilters = useFetchDynamicFilters()
  const fetchDynamicFiltersFacets = useFetchDynamicFiltersFacets()
  const fetchCatalogBrand = useFetchCatalogBrand()
  const {
    state: {
      dynamicFilters,
      data,
      filters,
      items: { searchTrackingParams: searchSession },
    },
    dispatch,
  } = useCatalogContext()
  const serverConfiguration = useCatalogConfigurationContext()

  const trackAbTest = useTrackAbTestCallback()
  const configuration = serverConfiguration
  const currency = filters.currency || configuration.config.currency

  const { globalSearchSessionId, globalCatalogBrowseSessionId } = searchSession
  const appliedCatalogsIds = filters.catalogIds
  const appliedSortOption = filters.sortBy
  const appliedPriceFrom = filters.priceFrom
  const appliedPriceTo = filters.priceTo
  const searchSessionId = searchSession.sessionId
  const dynamicFilterList = dynamicFilters.dynamicFilters
  const selectedDynamicFilterList = dynamicFilters.selectedDynamicFilters
  const temporarySelectedDynamicFilterList = dynamicFilters.temporarySelectedDynamicFilters
  const temporarySelectedCatalogIds = dynamicFilters.temporaryCatalogIds
  const temporarySelectedPricerange = dynamicFilters.temporaryPriceRange
  const totalFacetCount = dynamicFilters.filterFacets.filterFacetsCount
  const allCatalogs = useMemo(() => {
    return keyBy(data.catalogs, 'id')
  }, [data.catalogs])

  const [modalName, setModalName] = useState<string>(FilterModalNames.Default)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [totalItemsCount, setTotalItemsCount] = useState<number | null>(null)
  const [isLazyLoading, setIsLazyLoading] = useState(false)
  const [isAnyFilterModalOpen, setIsAnyFilterModalOpen] = useState(false)
  const [openedLazyFilterType, setOpenedLazyFilterType] = useState<string | null>(null)
  const [selectedSortOption, setSelectedSortOption] = useState<SortByOption | null | undefined>(
    appliedSortOption,
  )

  const [selectedCatalogsIds, setSelectedCatalogsIds] = useState(appliedCatalogsIds)
  const [selectedPriceRange, setSelectedPriceRange] = useState({
    priceFrom: appliedPriceFrom,
    priceTo: appliedPriceTo,
  })

  const shownFilters = useRef<Array<string>>([])

  useEffect(() => {
    window.addEventListener('popstate', reloadPage)

    return () => {
      window.removeEventListener('popstate', reloadPage)
    }
  }, [])

  useEffect(() => {
    if (modalName !== FilterModalNames.Root) return

    dynamicFilterList.forEach(filter => {
      if (
        filter.isHidden &&
        filter.experiments?.some(experiment => experiment.exposure_type === DynamicExposeType.View)
      ) {
        if (filter.experiments)
          trackExperimentsByType(filter.experiments, DynamicExposeType.View, trackAbTest)
      }
    })
  }, [dynamicFilterList, modalName, trackAbTest])

  useEffect(() => {
    if (modalName === FilterModalNames.Root) {
      fetchDynamicFilters({
        selectedDynamicFilters: temporarySelectedDynamicFilterList,
        selectedFilters: {
          ...temporarySelectedPricerange,
          sortBy: selectedSortOption,
          catalogIds: temporarySelectedCatalogIds,
          currency,
        },
        isTemporary: true,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalName])

  const handleItemFacetChange = useFacetChangeHandler({
    dynamicFiltersList: dynamicFilterList,
    setTotalItemsCount,
    fetchDynamicFiltersFacets,
    selectedTemporaryCatalogIds: selectedCatalogsIds,
    selectedTemporaryPriceRange: selectedPriceRange,
  })

  const recalculateTotalItemCount = useLatestCallback((filterType: string) => {
    handleItemFacetChange(temporarySelectedDynamicFilterList, filterType)
  })

  // new effect to trigger handleItemFacetChange after state update for lazy filters
  useEffect(() => {
    if (!openedLazyFilterType) return

    handleItemFacetChange(temporarySelectedDynamicFilterList, openedLazyFilterType)
    setOpenedLazyFilterType(null)
  }, [openedLazyFilterType, handleItemFacetChange, temporarySelectedDynamicFilterList])

  const clearSelectedFilters = () => {
    setSelectedSortOption(null)
    setSelectedCatalogsIds([])
    setSelectedPriceRange({ priceFrom: null, priceTo: null })
    setTotalItemsCount(null)

    dispatch({
      type: DynamicFilterReducerAction.SET_TEMPORARY_SELECTED_DYNAMIC_FILTERS,
      payload: { temporarySelectedDynamicFilters: [] },
    })

    dispatch({
      type: DynamicFilterReducerAction.SET_TEMPORARY_PRICE_RANGE,
      payload: { priceRange: { priceFrom: null, priceTo: null } },
    })

    dispatch({
      type: DynamicFilterReducerAction.SET_TEMPORARY_CATALOG_IDS,
      payload: { temporaryCatalogIds: [] },
    })

    dispatch({
      type: DynamicFilterReducerAction.SET_DYNAMIC_FILTER_FACETS_COUNTS,
      payload: { filterFacetsCount: null },
    })

    fetchDynamicFilters({
      selectedDynamicFilters: [],
      selectedTemporaryCatalogIds: [],
      selectedTemporaryPriceRange: { priceFrom: null, priceTo: null },
      isTemporary: true,
    })
  }

  const resetSelectedFilters = () => {
    setSelectedSortOption(appliedSortOption)
    setSelectedCatalogsIds(appliedCatalogsIds)
    setSelectedPriceRange({
      priceFrom: appliedPriceFrom,
      priceTo: appliedPriceTo,
    })
  }

  const updateTemporarySelectedFilters = () => {
    dispatch({
      type: DynamicFilterReducerAction.SET_TEMPORARY_SELECTED_DYNAMIC_FILTERS,
      payload: { temporarySelectedDynamicFilters: selectedDynamicFilterList },
    })

    dispatch({
      type: DynamicFilterReducerAction.SET_TEMPORARY_PRICE_RANGE,
      payload: { priceRange: selectedPriceRange },
    })

    dispatch({
      type: DynamicFilterReducerAction.SET_TEMPORARY_CATALOG_IDS,
      payload: { temporaryCatalogIds: appliedCatalogsIds },
    })
  }

  const openModal = () => {
    setModalName(FilterModalNames.Root)
    setIsModalOpen(true)

    updateTemporarySelectedFilters()
  }

  const closeModal = () => {
    setModalName(FilterModalNames.Default)
    setIsModalOpen(false)
    setIsLazyLoading(false)

    shownFilters.current = []
  }

  const trackCancelClick = () => {
    track(
      clickEvent({
        target: 'cancel_filters',
        screen: 'search_filter',
      }),
    )
  }

  const handleSubmit = () => {
    closeModal()

    fetchDynamicFilters({
      selectedDynamicFilters: temporarySelectedDynamicFilterList,
      selectedFilters: {
        ...selectedPriceRange,
        sortBy: selectedSortOption,
        catalogIds: selectedCatalogsIds,
        currency,
      },
    })
    fetchCatalogBrand({ selectedDynamicFilters: temporarySelectedDynamicFilterList })

    dynamicFilterList.forEach(filter => {
      if (
        !filter.experiments?.some(
          experiment => experiment.exposure_type === DynamicExposeType.Apply,
        )
      )
        return

      const selectedFilter = temporarySelectedDynamicFilterList.find(
        item => item.type === filter.type,
      )

      if (!selectedFilter?.ids?.length) return

      if (filter.experiments)
        trackExperimentsByType(filter.experiments, DynamicExposeType.Apply, trackAbTest)
    })
  }

  const handleSubmitWithTracking = () => {
    handleSubmit()

    track(
      clickEvent({
        target: 'apply_filters',
        screen: 'search_filter',
      }),
    )
  }

  const handleFilterClick =
    (filter: Filter, type?: string, experiments?: Array<ExperimentModel>, isLazy?: boolean) =>
    async () => {
      setModalName(`${FilterModalNames.Filter}_${filter}${type ? `_${type}` : ''}`)
      // reset filter facet count when entering a filter
      dispatch({
        type: DynamicFilterReducerAction.SET_DYNAMIC_FILTER_FACETS_COUNTS,
        payload: { filterFacetsCount: null },
      })

      // fetch options if filter is lazy
      if (type && isLazy) {
        setIsLazyLoading(true)
        await fetchDynamicFiltersFacets({
          selectedDynamicFilters: temporarySelectedDynamicFilterList,
          selectedTemporaryCatalogIds: temporarySelectedCatalogIds,
          selectedTemporaryPriceRange: temporarySelectedPricerange,
          filterCode: type,
          isLazy: true,
        })
        setIsLazyLoading(false)

        // defer the calculation to the effect after re-render
        setOpenedLazyFilterType(type)
      } else if (type) {
        handleItemFacetChange(temporarySelectedDynamicFilterList, type)
      }

      track(
        clickFilterEvent({
          filter,
          screen: 'search_filter',
          searchSessionId,
          globalSearchSessionId,
          globalCatalogBrowseSessionId,
          type,
          code: type,
        }),
      )

      if (experiments) trackExperimentsByType(experiments, DynamicExposeType.Click, trackAbTest)
    }

  const trackClearFiltersClick = () => {
    track(
      clickEvent({
        target: 'clear_filters',
        screen: 'search_filter',
      }),
    )
  }

  const trackFilterView = (filter: Filter, type?: string, experiments?: Array<ExperimentModel>) => {
    track(
      viewFilterEvent({
        filter,
        screen: 'search_filter',
        searchSessionId,
        globalSearchSessionId,
        globalCatalogBrowseSessionId,
        type,
        code: type,
      }),
    )

    if (experiments) trackExperimentsByType(experiments, DynamicExposeType.View, trackAbTest)
  }

  const trackFilterCellClick = () => {
    track(
      clickEvent({
        target: 'select_filters',
      }),
    )
  }

  const handleFilterCellView =
    (filter: Filter, type?: string, experiments?: Array<ExperimentModel>) => () => {
      const shownFilter = type || filter

      if (shownFilters.current.includes(shownFilter)) return

      shownFilters.current.push(shownFilter)

      trackFilterView(filter, type, experiments)
    }

  const handleFilterCellClick = () => {
    openModal()
    trackFilterCellClick()
  }

  const handlePrefixClick = () => {
    closeModal()
    resetSelectedFilters()
    trackCancelClick()
  }

  const handleSuffixClick = () => {
    clearSelectedFilters()
    trackClearFiltersClick()
  }

  const handleCatalogSelect = (newCatalogIds: Array<number>) => {
    setSelectedCatalogsIds(newCatalogIds)

    dispatch({
      type: DynamicFilterReducerAction.SET_TEMPORARY_CATALOG_IDS,
      payload: { temporaryCatalogIds: newCatalogIds },
    })
  }

  const handlePriceRange = (range: PriceRange) => {
    setSelectedPriceRange(range)

    dispatch({
      type: DynamicFilterReducerAction.SET_TEMPORARY_SELECTED_DYNAMIC_FILTERS,
      payload: { temporarySelectedDynamicFilters: temporarySelectedDynamicFilterList },
    })

    dispatch({
      type: DynamicFilterReducerAction.SET_TEMPORARY_PRICE_RANGE,
      payload: { priceRange: range },
    })

    dispatch({
      type: FilterReducerAction.CHANGE_FILTERS,
      payload: {
        filters: { ...filters, currency },
      },
    })
  }

  const handleFilterUpdate = useLatestCallback(
    (updatedSelectedFilters: Array<SelectedDynamicFilterModel>, type: string) => {
      dispatch({
        type: DynamicFilterReducerAction.SET_TEMPORARY_SELECTED_DYNAMIC_FILTERS,
        payload: { temporarySelectedDynamicFilters: updatedSelectedFilters },
      })

      handleItemFacetChange(updatedSelectedFilters, type)
    },
  )

  const handleAddSelectedFilter = useLatestCallback(
    (newFilter: { type: string; ids: Array<number> }) => {
      const updatedSelectedFilters = addSelectedFilter(
        newFilter,
        temporarySelectedDynamicFilterList,
      )

      handleFilterUpdate(updatedSelectedFilters, newFilter.type)
    },
  )

  const handleRemoveSelectedFilter = useLatestCallback(
    (removeFilter: { type: string; id: number }) => {
      const updatedSelectedFilters = removeSelectedFilter(
        removeFilter,
        temporarySelectedDynamicFilterList,
      )

      handleFilterUpdate(updatedSelectedFilters, removeFilter.type)
    },
  )

  const handleRemoveSelectedFilters = useLatestCallback(
    (removeFilters: { type: string; ids?: Array<number> }) => {
      const updatedSelectedFilters = removeSelectedFilters(
        removeFilters,
        temporarySelectedDynamicFilterList,
      )

      handleFilterUpdate(updatedSelectedFilters, removeFilters.type)
    },
  )

  const handleDynamicFilterToggle = useLatestCallback(
    (id: number, type: string, selectionType: SelectionType, selectedIds: Array<number>) => {
      if (!selectedIds.includes(id)) {
        const newIds = selectionType === SelectionType.Single ? [id] : [...selectedIds, id]
        handleAddSelectedFilter({ type, ids: newIds })
      } else if (selectionType === SelectionType.Multi) {
        handleRemoveSelectedFilter({ type, id })
      }
    },
  )

  const clearDynamicFilterOptions = (type: string, ids?: Array<number>) => {
    handleRemoveSelectedFilters({ type, ids })
  }

  const handleAnyFilterModalOpen = () => {
    setIsAnyFilterModalOpen(true)
  }

  const handleAnyFilterModalClose = () => {
    setModalName(FilterModalNames.Root)
    shownFilters.current = []
    setIsAnyFilterModalOpen(false)
  }

  const getTemporarySelectedFilter = (type: string) =>
    temporarySelectedDynamicFilterList.find(filter => filter.type === type)

  const getRootOptionType = (dynamicFilter: DynamicFilterModel) =>
    dynamicFilter.options.find(option => !!option.type)?.type || OptionType.Default

  const renderModalNavigation = () => (
    <FilterModalNavigation
      title={translate('mobile.filters_screen.title')}
      icon={<Icon name={X24} />}
      prefixAriaLabel={translate('a11y.actions.close')}
      suffixText={translate('mobile.filters_screen.actions.clear_filters')}
      testId="modal-navigation"
      onPrefixClick={handlePrefixClick}
      onSuffixClick={handleSuffixClick}
    />
  )

  const renderDynamicFilter = (filter: DynamicFilterModel, sharedProps: SharedFilterProps) => {
    const temporarySelectedDynamicFilter = getTemporarySelectedFilter(filter.type)
    const key = `${filter.type}-${filter.id}`
    const noOptionSelected =
      !temporarySelectedDynamicFilter || temporarySelectedDynamicFilter?.ids.length === 0
    let totalFacetsCount: number | null | undefined

    if (filter.isAsyncFacet) {
      totalFacetsCount = noOptionSelected ? null : totalFacetCount
    } else {
      totalFacetsCount = noOptionSelected || totalItemsCount == null ? null : totalItemsCount
    }

    switch (filter.displayType) {
      case DisplayType.List: {
        return (
          <ListFilterModal
            key={key}
            type={filter.type}
            title={filter.title}
            selectionType={filter.selectionType}
            isSelectionHighlighted={filter.isSelectionHighlighted}
            isNewFilter={filter.isNewFilter}
            options={filter.options}
            fromScreen="search_filter"
            isHidden={filter.isHidden}
            links={filter.links}
            totalItemsCount={totalFacetsCount}
            totalCountMax={filter.totalCountMax}
            isAsyncFacet={filter.isAsyncFacet}
            isLazy={filter.isLazy}
            isLoading={isLazyLoading}
            translations={filter.translations}
            rootOptionType={getRootOptionType(filter)}
            onSubmit={handleSubmit}
            onFilterCellClick={handleFilterClick(
              Filter.Dynamic,
              filter.type,
              filter.experiments,
              filter.isLazy,
            )}
            onFilterCellView={handleFilterCellView(Filter.Dynamic, filter.type, filter.experiments)}
            selectedIds={temporarySelectedDynamicFilter?.ids || []}
            clearSelectedOptions={clearDynamicFilterOptions}
            onListOptionSelect={id => {
              handleDynamicFilterToggle(
                id,
                filter.type,
                filter.selectionType,
                temporarySelectedDynamicFilter?.ids || [],
              )
            }}
            {...sharedProps}
          />
        )
      }
      case DisplayType.ListSearch: {
        return (
          <SearchFilterModal
            key={key}
            type={filter.type}
            title={filter.title}
            selectionType={filter.selectionType}
            isSelectionHighlighted={filter.isSelectionHighlighted}
            options={filter.options}
            fromScreen="search_filter"
            searchTranslations={filter.searchTranslations}
            isHidden={filter.isHidden}
            links={filter.links}
            totalItemsCount={totalFacetsCount}
            totalCountMax={filter.totalCountMax}
            isAsyncFacet={filter.isAsyncFacet}
            isLazy={filter.isLazy}
            isLoading={isLazyLoading}
            translations={filter.translations}
            rootOptionType={getRootOptionType(filter)}
            onSubmit={handleSubmit}
            onFilterCellClick={handleFilterClick(
              Filter.Dynamic,
              filter.type,
              filter.experiments,
              filter.isLazy,
            )}
            onFilterCellView={handleFilterCellView(Filter.Dynamic, filter.type, filter.experiments)}
            selectedIds={temporarySelectedDynamicFilter?.ids || []}
            clearSelectedOptions={clearDynamicFilterOptions}
            onItemCountRecalculate={recalculateTotalItemCount}
            onListOptionSelect={id => {
              handleDynamicFilterToggle(
                id,
                filter.type,
                filter.selectionType,
                temporarySelectedDynamicFilter?.ids || [],
              )
            }}
            {...sharedProps}
          />
        )
      }
      case DisplayType.Grid: {
        return (
          <GridFilterModal
            key={key}
            type={filter.type}
            title={filter.title}
            isSelectionHighlighted={filter.isSelectionHighlighted}
            isNewFilter={filter.isNewFilter}
            options={filter.options}
            isHidden={filter.isHidden}
            fromScreen="search_filter"
            totalItemsCount={totalFacetsCount}
            rootOptionType={getRootOptionType(filter)}
            onSubmit={handleSubmit}
            onFilterCellClick={handleFilterClick(Filter.Dynamic, filter.type, filter.experiments)}
            onFilterCellView={handleFilterCellView(Filter.Dynamic, filter.type, filter.experiments)}
            selectedIds={temporarySelectedDynamicFilter?.ids || []}
            clearSelectedOptions={clearDynamicFilterOptions}
            onListOptionSelect={id => {
              handleDynamicFilterToggle(
                id,
                filter.type,
                filter.selectionType,
                temporarySelectedDynamicFilter?.ids || [],
              )
            }}
            {...sharedProps}
          />
        )
      }
      case DisplayType.HybridPrice: {
        return (
          <PriceFilter
            key={key}
            selectedPriceRange={selectedPriceRange}
            minimumValidation={filter.minimumValidation}
            onPriceRangeChange={handlePriceRange}
            onSubmit={handleSubmitWithTracking}
            onFilterCellClick={handleFilterClick(Filter.Price)}
            onFilterCellView={handleFilterCellView(Filter.Price)}
            {...sharedProps}
          />
        )
      }
      default:
        return null
    }
  }

  const renderDynamicFilters = (sharedProps: SharedFilterProps) => {
    return dynamicFilterList.map(filter => renderDynamicFilter(filter, sharedProps))
  }

  const renderFilters = () => {
    const sharedProps = {
      isFilterCellVisible: !isAnyFilterModalOpen,
      onModalOpen: handleAnyFilterModalOpen,
      onModalClose: handleAnyFilterModalClose,
    }

    return (
      <SeparatedList separator={<Divider />}>
        <SortFilter
          learnMoreUrl={learnMoreUrl}
          selectedOption={selectedSortOption}
          onOptionClick={setSelectedSortOption}
          onSubmit={handleSubmitWithTracking}
          onFilterCellClick={handleFilterClick(Filter.Sort)}
          onFilterCellView={handleFilterCellView(Filter.Sort)}
          {...sharedProps}
        />
        <CatalogFilter
          allCatalogs={allCatalogs}
          selectedCatalogsIds={selectedCatalogsIds}
          onCatalogSelect={handleCatalogSelect}
          onSubmit={handleSubmitWithTracking}
          onFilterCellClick={handleFilterClick(Filter.Category)}
          onFilterCellView={handleFilterCellView(Filter.Category)}
          {...sharedProps}
        />
        {renderDynamicFilters(sharedProps)}
      </SeparatedList>
    )
  }

  const getAppliedFiltersCount = () => {
    let count = 0

    if (appliedCatalogsIds.length) count += 1
    if (appliedSortOption && appliedSortOption !== SortByOption.Relevance) count += 1
    if (appliedPriceFrom || appliedPriceTo) count += 1

    selectedDynamicFilterList.forEach(filter => {
      if (filter?.ids.length) count += 1
    })

    return count
  }

  const appliedFiltersCount = getAppliedFiltersCount()

  return (
    <>
      <Chip
        prefix={<Icon name={Filters16} />}
        radius={Chip.Radius.Round}
        text={translate('mobile.filters_chip_text', {
          count: appliedFiltersCount ? `(${appliedFiltersCount})` : null,
        })}
        textType={Text.Type.Subtitle}
        activated={!!appliedFiltersCount}
        onClick={handleFilterCellClick}
        testId="trigger"
      />
      <FilterModal
        isOpen={isModalOpen}
        testId="modal"
        renderNavigation={renderModalNavigation}
        onSubmit={handleSubmitWithTracking}
      >
        {renderFilters()}
        <Divider />
      </FilterModal>
    </>
  )
}

export default MobileFilters
